import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  increment
} from 'firebase/firestore';
import { db } from '../firebase';
import { Order, OrderItem, OrderStatus, PaymentStatus, Address, ShippingDetails } from '../types';

const toDate = (timestamp: Timestamp | undefined): Date => {
  return timestamp?.toDate() || new Date();
};

const generateOrderNumber = (): string => {
  const now = new Date();
  const year = now.getFullYear().toString().slice(-2);
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  return `YS${year}${month}${day}-${random}`;
};

// ================== ORDER QUERIES ==================

export const getPendingOrderCount = async (): Promise<number> => {
  const q = query(
    collection(db, 'orders'),
    where('status', '==', 'pending')
  );
  const snapshot = await getDocs(q);
  return snapshot.size;
};

export const getOrders = async (): Promise<Order[]> => {
  const q = query(
    collection(db, 'orders'),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      orderNumber: data.orderNumber,
      retailerId: data.retailerId,
      retailerName: data.retailerName,
      retailerEmail: data.retailerEmail || '',
      items: data.items as OrderItem[],
      subtotal: data.subtotal,
      shipping: data.shipping || 0,
      tax: data.tax || 0,
      total: data.total,
      status: data.status as OrderStatus,
      paymentStatus: data.paymentStatus as PaymentStatus,
      shippingAddress: data.shippingAddress as Address,
      trackingNumber: data.trackingNumber,
      createdAt: toDate(data.createdAt),
      updatedAt: data.updatedAt ? toDate(data.updatedAt) : undefined
    } as Order;
  });
};

export const getOrder = async (id: string): Promise<Order | null> => {
  const docRef = doc(db, 'orders', id);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;

  const data = docSnap.data();
  return {
    id: docSnap.id,
    orderNumber: data.orderNumber,
    retailerId: data.retailerId,
    retailerName: data.retailerName,
    items: data.items as OrderItem[],
    subtotal: data.subtotal,
    shipping: data.shipping || 0,
    tax: data.tax || 0,
    total: data.total,
    status: data.status as OrderStatus,
    paymentStatus: data.paymentStatus as PaymentStatus,
    shippingAddress: data.shippingAddress as Address,
    trackingNumber: data.trackingNumber,
    createdAt: toDate(data.createdAt),
    updatedAt: data.updatedAt ? toDate(data.updatedAt) : undefined
  } as Order;
};

export const getOrdersByRetailer = async (retailerId: string): Promise<Order[]> => {
  const q = query(
    collection(db, 'orders'),
    where('retailerId', '==', retailerId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      orderNumber: data.orderNumber,
      retailerId: data.retailerId,
      retailerName: data.retailerName,
      retailerEmail: data.retailerEmail || '',
      items: data.items as OrderItem[],
      subtotal: data.subtotal,
      shipping: data.shipping || 0,
      tax: data.tax || 0,
      total: data.total,
      status: data.status as OrderStatus,
      paymentStatus: data.paymentStatus as PaymentStatus,
      shippingAddress: data.shippingAddress as Address,
      trackingNumber: data.trackingNumber,
      createdAt: toDate(data.createdAt),
      updatedAt: data.updatedAt ? toDate(data.updatedAt) : undefined
    } as Order;
  });
};

// ================== ORDER MUTATIONS ==================

interface CreateOrderParams {
  retailerId?: string;
  retailerName: string;
  retailerEmail: string;
  items: OrderItem[];
  subtotal: number;
  shipping?: number;
  tax?: number;
  shippingAddress: Address;
  shippingDetails?: ShippingDetails;
}

export const createOrder = async (params: CreateOrderParams): Promise<string> => {
  const orderNumber = generateOrderNumber();
  const total = params.subtotal + (params.shipping || 0) + (params.tax || 0);

  const orderData: Record<string, any> = {
    orderNumber,
    retailerId: params.retailerId || '',
    retailerName: params.retailerName,
    retailerEmail: params.retailerEmail,
    items: params.items,
    subtotal: params.subtotal,
    shipping: params.shipping || 0,
    tax: params.tax || 0,
    total,
    status: 'pending' as OrderStatus,
    paymentStatus: 'awaiting_transfer' as PaymentStatus,
    shippingAddress: params.shippingAddress,
    createdAt: serverTimestamp()
  };

  if (params.shippingDetails) {
    orderData.shippingDetails = params.shippingDetails;
  }

  await addDoc(collection(db, 'orders'), orderData);

  if (params.retailerId) {
    const retailerRef = doc(db, 'retailers', params.retailerId);
    await updateDoc(retailerRef, {
      totalOrders: increment(1),
      totalSpent: increment(total)
    });
  }

  return orderNumber;
};

export const updateOrderStatus = async (
  orderId: string,
  status: OrderStatus,
  trackingNumber?: string
): Promise<void> => {
  const updateData: any = {
    status,
    updatedAt: serverTimestamp()
  };

  if (trackingNumber) {
    updateData.trackingNumber = trackingNumber;
  }

  await updateDoc(doc(db, 'orders', orderId), updateData);
};

export const updatePaymentStatus = async (
  orderId: string,
  paymentStatus: PaymentStatus
): Promise<void> => {
  const updateData: any = {
    paymentStatus,
    updatedAt: serverTimestamp()
  };

  if (paymentStatus === 'received') {
    updateData.status = 'payment_received';
  }

  await updateDoc(doc(db, 'orders', orderId), updateData);
};

// ================== ANALYTICS ==================

export interface OrderStats {
  totalOrders: number;
  pendingOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
  ordersByStatus: Record<OrderStatus, number>;
}

export const getOrderStats = async (): Promise<OrderStats> => {
  const orders = await getOrders();

  const stats: OrderStats = {
    totalOrders: orders.length,
    pendingOrders: 0,
    totalRevenue: 0,
    averageOrderValue: 0,
    ordersByStatus: {
      pending: 0,
      payment_received: 0,
      processing: 0,
      shipped: 0,
      delivered: 0
    }
  };

  orders.forEach(order => {
    stats.totalRevenue += order.total;
    stats.ordersByStatus[order.status]++;
    if (order.status === 'pending') {
      stats.pendingOrders++;
    }
  });

  stats.averageOrderValue = orders.length > 0 ? stats.totalRevenue / orders.length : 0;

  return stats;
};
